import {
  type ColumnDef,
  type SortingState,
  type RowSelectionState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useState } from "react";
import { ChevronUp, ChevronDown, ChevronsUpDown, Loader2 } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/v2/empty-state";
import { ListErrorState } from "@/components/v2/list-error-state";
import { cn } from "@/lib/utils";

// Extend column meta to support numeric flag
declare module "@tanstack/react-table" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData, TValue> {
    numeric?: boolean;
  }
}

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  total?: number;
  isLoading?: boolean;
  isError?: boolean;
  errorEntityLabel?: string;
  onRetry?: () => void;
  onRowClick?: (row: TData) => void;
  emptyMessage?: string;
  emptyDescription?: string;
  pageSize?: number;
  pageIndex?: number;
  onPaginationChange?: (pageIndex: number) => void;
  sorting?: SortingState;
  onSortingChange?: (sorting: SortingState) => void;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  total,
  isLoading,
  isError,
  errorEntityLabel = "results",
  onRetry,
  onRowClick,
  emptyMessage = "No results found",
  emptyDescription = "Try adjusting your search or filters.",
  pageSize = 50,
  pageIndex = 0,
  onPaginationChange,
  sorting: controlledSorting,
  onSortingChange,
}: DataTableProps<TData, TValue>) {
  const [internalSorting, setInternalSorting] = useState<SortingState>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const isControlledSorting = controlledSorting !== undefined;
  const activeSorting = isControlledSorting
    ? controlledSorting
    : internalSorting;

  // Prepend checkbox selection column
  const allColumns: ColumnDef<TData, TValue>[] = [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && "indeterminate")
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          onClick={(e) => e.stopPropagation()}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      size: 40,
    } as ColumnDef<TData, TValue>,
    ...columns,
  ];

  const table = useReactTable({
    data,
    columns: allColumns,
    state: {
      sorting: activeSorting,
      rowSelection,
    },
    onSortingChange: (updater) => {
      const next =
        typeof updater === "function" ? updater(activeSorting) : updater;
      if (onSortingChange) {
        onSortingChange(next);
      } else {
        setInternalSorting(next);
      }
    },
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: isControlledSorting ? undefined : getSortedRowModel(),
    manualSorting: isControlledSorting,
    enableRowSelection: true,
  });

  // Pagination calculations
  const displayTotal = total ?? data.length;
  const showFrom = displayTotal > 0 ? pageIndex * pageSize + 1 : 0;
  const showTo = Math.min(pageIndex * pageSize + data.length, displayTotal);
  const totalPages = Math.ceil(displayTotal / pageSize);
  const hasPagination = displayTotal > 0;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16" role="status">
        <Loader2 className="h-8 w-8 animate-spin text-[color:var(--color-text-lo)]" />
      </div>
    );
  }

  if (isError) {
    return <ListErrorState entityLabel={errorEntityLabel} onRetry={onRetry} />;
  }

  if (data.length === 0) {
    return <EmptyState title={emptyMessage} description={emptyDescription} />;
  }

  return (
    <div>
      <div className="rounded-md border border-[color:var(--color-border)] overflow-hidden">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow
                key={headerGroup.id}
                className="h-9 border-b border-[color:var(--color-border)]"
              >
                {headerGroup.headers.map((header) => {
                  const isNumeric = header.column.columnDef.meta?.numeric;
                  const canSort = header.column.getCanSort();
                  const sorted = header.column.getIsSorted();

                  return (
                    <TableHead
                      key={header.id}
                      className={cn(
                        "typo-label text-[color:var(--color-text-md)] px-4 py-2 h-9",
                        isNumeric && "text-right",
                        header.column.id === "select" && "w-10 pr-0",
                      )}
                      style={
                        header.column.id === "select"
                          ? { width: 40 }
                          : undefined
                      }
                    >
                      {header.isPlaceholder ? null : canSort ? (
                        <button
                          className={cn(
                            "flex items-center gap-1 hover:text-[color:var(--color-text-hi)] transition-colors",
                            isNumeric && "ml-auto",
                          )}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                          <span className="flex-shrink-0" aria-hidden="true">
                            {sorted === "asc" && (
                              <ChevronUp className="h-3 w-3" />
                            )}
                            {sorted === "desc" && (
                              <ChevronDown className="h-3 w-3" />
                            )}
                            {!sorted && (
                              <ChevronsUpDown className="h-3 w-3 text-[color:var(--color-text-lo)]/50" />
                            )}
                          </span>
                        </button>
                      ) : (
                        flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                className={cn(
                  "h-9 border-b border-[color:var(--color-border)] last:border-0 transition-colors group",
                  onRowClick &&
                    "cursor-pointer hover:bg-[color:var(--color-elevated)]",
                )}
                data-state={row.getIsSelected() ? "selected" : undefined}
                onClick={() => onRowClick?.(row.original)}
              >
                {row.getVisibleCells().map((cell) => {
                  const isNumeric = cell.column.columnDef.meta?.numeric;
                  return (
                    <TableCell
                      key={cell.id}
                      className={cn(
                        "px-4 py-1.5 align-middle text-[color:var(--color-text-hi)]",
                        isNumeric
                          ? "typo-mono text-right tabular-nums"
                          : "typo-body",
                        cell.column.id === "select" && "w-10 pr-0",
                      )}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {hasPagination && (
        <div className="flex items-center justify-between px-2 py-4">
          <p className="typo-body text-[color:var(--color-text-md)]">
            Showing {showFrom} to {showTo} of {displayTotal.toLocaleString()}
          </p>
          {totalPages > 1 && onPaginationChange && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={pageIndex <= 0}
                onClick={() => onPaginationChange(pageIndex - 1)}
              >
                Prev
              </Button>
              <span className="typo-body text-[color:var(--color-text-md)]">
                Page {pageIndex + 1} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={pageIndex >= totalPages - 1}
                onClick={() => onPaginationChange(pageIndex + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
